import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import walletService from '../../services/api/walletService';
import './Wallet.css';

const Wallet = () => {
  const navigate = useNavigate();
  const [wallet, setWallet] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [topUpAmount, setTopUpAmount] = useState('');
  const [showTopUpModal, setShowTopUpModal] = useState(false);
  const [filter, setFilter] = useState('all');
  const [error, setError] = useState('');

  useEffect(() => {
    fetchWalletData();
  }, []);

  useEffect(() => {
    fetchTransactions(filter);
  }, [filter]);

  const fetchWalletData = async () => {
    try {
      setLoading(true);
      const [walletRes, summaryRes] = await Promise.all([
        walletService.getWallet(),
        walletService.getSummary(),
      ]);
      
      setWallet(walletRes.data.wallet);
      setSummary(summaryRes.data);
      setTransactions(walletRes.data.recentTransactions);
      setError('');
    } catch (err) {
      console.error('Error fetching wallet:', err);
      setError('Failed to load wallet. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const fetchTransactions = async (category) => {
    try {
      const params = category === 'all' ? {} : { category };
      const response = await walletService.getTransactions(params);
      setTransactions(response.data.transactions);
    } catch (err) {
      console.error('Error fetching transactions:', err);
    }
  };

  const handleTopUp = async (e) => {
    e.preventDefault();
    setError('');

    const amount = parseFloat(topUpAmount);
    const minAmount = 10000; // Could be made dynamic based on currency
    
    if (isNaN(amount) || amount < minAmount) {
      setError(`Minimum top-up amount is ${formatAmount(minAmount)}`);
      return;
    }

    try {
      const response = await walletService.initiateTopUp(amount);
      
      // Redirect to Flutterwave payment page
      if (response.data.paymentLink) {
        window.location.href = response.data.paymentLink;
      }
    } catch (err) {
      console.error('Error initiating top-up:', err);
      setError(err.response?.data?.error || 'Failed to initiate top-up');
    }
  };

 const formatAmount = (amount, currency = null) => {
    const curr = currency || wallet?.currency || 'UGX';
    return new Intl.NumberFormat('en-US', {
      style: 'currency', 
      currency: curr,
      currencyDisplay: 'code',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('en-UG', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getTransactionIcon = (category, type) => {
    if (category === 'topup') return '💰';
    if (category === 'vote') return '🗳️';
    if (category === 'refund') return '↩️';
    return type === 'credit' ? '➕' : '➖';
  };

  if (loading) {
    return (
      <div className="wallet-container">
        <div className="loading">Loading wallet...</div>
      </div>
    );
  }

  return (
    <div className="wallet-container">
      <div className="wallet-header">
        <h1>My Wallet</h1>
        <button className="btn-back" onClick={() => navigate(-1)}>
          ← Back
        </button>
      </div>

      {error && <div className="error-message">{error}</div>}

      {/* Wallet Balance Card */}
      <div className="wallet-balance-card">
        <div className="balance-section">
          <p className="balance-label">Current Balance</p>
          <h2 className="balance-amount">{formatAmount(wallet?.balance || 0)}</h2>
          <p className="balance-currency">{wallet?.currency}</p>
        </div>
        <button 
          className="btn-topup" 
          onClick={() => setShowTopUpModal(true)}
        >
          💳 Top Up Wallet
        </button>
      </div>

      {/* Summary Cards */}
      {summary && (
        <div className="summary-cards">
          <div className="summary-card">
            <div className="summary-icon">💸</div>
            <div className="summary-content">
              <p className="summary-label">Total Loaded</p>
              <p className="summary-value">{formatAmount(summary.totalTopUp)}</p>
              <p className="summary-count">{summary.totalTopUps} transactions</p>
            </div>
          </div>
          <div className="summary-card">
            <div className="summary-icon">🗳️</div>
            <div className="summary-content">
              <p className="summary-label">Total Spent</p>
              <p className="summary-value">{formatAmount(summary.totalSpent)}</p>
              <p className="summary-count">{summary.totalVotes} votes</p>
            </div>
          </div>
        </div>
      )}

      {/* Transaction History */}
      <div className="transaction-section">
        <div className="section-header">
          <h3>Transaction History</h3>
          <div className="filter-tabs">
            <button 
              className={filter === 'all' ? 'active' : ''} 
              onClick={() => setFilter('all')}
            >
              All
            </button>
            <button 
              className={filter === 'topup' ? 'active' : ''} 
              onClick={() => setFilter('topup')}
            >
              Top-ups
            </button>
            <button 
              className={filter === 'vote' ? 'active' : ''} 
              onClick={() => setFilter('vote')}
            >
              Usage
            </button>
          </div>
        </div>

        <div className="transactions-list">
          {transactions.length === 0 ? (
            <div className="empty-state">
              <p>No transactions yet</p>
              <p className="empty-subtext">Top up your wallet to get started</p>
            </div>
          ) : (
            transactions.map((tx) => (
              <div key={tx.id} className="transaction-item">
                <div className="tx-icon">
                  {getTransactionIcon(tx.category, tx.type)}
                </div>
                <div className="tx-details">
                  <p className="tx-description">{tx.description}</p>
                  <p className="tx-date">{formatDate(tx.createdAt)}</p>
                </div>
                <div className={`tx-amount ${tx.type}`}>
                  <span className="tx-sign">{tx.type === 'credit' ? '+' : '-'}</span>
                  {formatAmount(tx.amount)}
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Top Up Modal */}
      {showTopUpModal && (
        <div className="modal-overlay" onClick={() => setShowTopUpModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Top Up Wallet</h3>
              <button className="modal-close" onClick={() => setShowTopUpModal(false)}>
                ×
              </button>
            </div>
            <form onSubmit={handleTopUp}>
              <div className="form-group">
                <label>Amount ({wallet?.currency || 'UGX'})</label>
                <input
                  type="number"
                  value={topUpAmount}
                  onChange={(e) => setTopUpAmount(e.target.value)}
                  placeholder={`Minimum ${formatAmount(10000)}`}
                  min="10000"
                  step="1000"
                  required
                />
                <p className="input-hint">Minimum top-up: {formatAmount(10000)}</p>
              </div>
              
              <div className="quick-amounts">
                <p>Quick amounts:</p>
                <div className="quick-buttons">
                  <button type="button" onClick={() => setTopUpAmount('10000')}>10,000</button>
                  <button type="button" onClick={() => setTopUpAmount('20000')}>20,000</button>
                  <button type="button" onClick={() => setTopUpAmount('50000')}>50,000</button>
                  <button type="button" onClick={() => setTopUpAmount('100000')}>100,000</button>
                </div>
              </div>

              {error && <div className="error-message">{error}</div>}

              <div className="modal-actions">
                <button 
                  type="button" 
                  className="btn-cancel" 
                  onClick={() => setShowTopUpModal(false)}
                >
                  Cancel
                </button>
                <button type="submit" className="btn-submit">
                  Proceed to Payment
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Wallet;
